
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import moment, { Moment } from 'moment'
import Testable from '@/mixins/Testable'
import { namespace } from 'vuex-class'

const Auth = namespace('auth')

@Component
export default class DateTimePicker extends Mixins(Testable) {
	@Prop({ required: true })
	value!: Moment

	@Prop({ required: false })
	pickerUsedFor!: string

	@Prop({ required: false })
	mode!: string

	@Prop({ required: false, default: true, type: Boolean })
	fiveMinuteInterval!: boolean

	@Prop({ required: false, default: false, type: Boolean })
	subtractingAlwaysAllowed!: boolean

	@Auth.Getter('firstDayOfWeek')
	firstDayOfWeek!: string

	datepickerModal: boolean = false
	datepickerDate: string = ''
	timePicker: string = ''

	created() {
		if (
			this.pickerUsedFor === 'vesselLogs' ||
			this.pickerUsedFor === 'gasOilUsages' ||
			this.pickerUsedFor === 'fleetMembers'
		) {
			this.datepickerDate = moment(this.value).format('YYYY-MM-DD')
		}
	}

	@Watch('datepickerDate')
	datepickerDateChange() {
		if (
			this.pickerUsedFor === 'vesselLogs' ||
			this.pickerUsedFor === 'gasOilUsages' ||
			this.pickerUsedFor === 'fleetMembers'
		) {
			if (this.datepickerDate === moment(this.value).format('YYYY-MM-DD')) {
				return
			}
		}
		if (this.mode === 'start' || this.pickerUsedFor === 'fleetMembers') {
			this.$emit('newDateSelected', moment(this.datepickerDate).hour(0).minutes(0))
		} else {
			this.$emit('newDateSelected', moment(this.datepickerDate).hour(23).minutes(59))
		}
	}

	@Watch('value')
	setDatepickerDate() {
		if (this.pickerUsedFor === 'vesselLogs' || this.pickerUsedFor === 'gasOilUsages') {
			if (moment(this.datepickerDate).format() !== moment(this.value).format()) {
				this.datepickerDate = moment(this.value).format('YYYY-MM-DD')
			}
		}
	}

	subtractTime(amount, unit, canSubtractProperty, addRemainder = false) {
		if (!this[canSubtractProperty]) {
			return
		}

		const date =
			this.datepickerDate && this.timePicker
				? moment(`${this.datepickerDate} ${this.timePicker}`, 'YYYY-MM-DD HH:mm')
				: this.value.clone()

		if (addRemainder && this.remainder && unit === 'minutes') {
			date.add(5 - this.remainder, 'minutes')
		}

		const newDateTime = date.subtract(amount, unit)

		this.$emit('input', newDateTime)

		this.datepickerDate = newDateTime.format('YYYY-MM-DD')
		this.timePicker = newDateTime.format('HH:mm')
	}

	subtractHour() {
		this.subtractTime(1, 'hours', 'canSubtractHour')
	}

	subtractMinute() {
		this.subtractTime(1, 'minute', 'canSubtractMinute')
	}

	subtractFiveMinutes() {
		this.subtractTime(5, 'minutes', 'canSubtractMinute', true)
	}

	addTime(amount, unit, subtractRemainder = false) {
		const dateTime =
			this.datepickerDate && this.timePicker
				? moment(`${this.datepickerDate} ${this.timePicker}`, 'YYYY-MM-DD HH:mm')
				: this.value.clone()

		if (subtractRemainder) {
			dateTime.subtract(this.remainder, 'minutes').add(amount, unit)
		} else {
			dateTime.add(amount, unit)
		}

		this.$emit('input', dateTime)

		this.datepickerDate = dateTime.format('YYYY-MM-DD')
		this.timePicker = dateTime.format('HH:mm')
	}

	addHour() {
		this.addTime(1, 'hours')
	}

	addMinute() {
		this.addTime(1, 'minute')
	}

	addFiveMinutes() {
		this.addTime(5, 'minutes', true)
	}

	get canSubtractHour() {
		if (this.subtractingAlwaysAllowed) {
			return true
		}

		const temp = this.value.clone().subtract(1, 'hours')
		const now = moment()

		return temp >= now
	}

	get canSubtractMinute() {
		if (this.subtractingAlwaysAllowed) {
			return true
		}
		const temp = this.value.clone().subtract(1, 'minute')
		const now = moment()

		return temp >= now
	}

	get remainder() {
		return moment(this.timePicker, 'HH:mm').minute() % 5
	}

	confirm() {
		const combinedDateTime = moment(`${this.datepickerDate} ${this.timePicker}`, 'YYYY-MM-DD HH:mm')
		this.$emit('input', combinedDateTime)
		this.datepickerModal = false
	}

	get formattedDatePicker() {
		if (!this.datepickerDate) {
			return this.value.format('ddd D MMM')
		} else {
			return moment(this.datepickerDate, 'YYYY-MM-DD').format('ddd D MMM')
		}
	}
}
